import styled from "styled-components";
import { Link } from "react-router-dom";

/* ── Hero ── */
export const PageHeader = styled.div`
  position: relative;
  padding-top: calc(4rem + 90px);
  padding-bottom: calc(${({ theme }) => theme.spacing["3xl"]} + 30px);
  background: linear-gradient(
      to bottom,
      rgba(16, 22, 34, 0.6) 0%,
      rgba(16, 22, 34, 0.82) 55%,
      rgba(16, 22, 34, 0.95) 100%
    )
    ${({ $image }) =>
      $image ? `, url(${$image}) center / cover no-repeat` : ""};
  background-color: ${({ theme }) => theme.colors.navy};
  text-align: center;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background:
      radial-gradient(
        circle at 50% 0%,
        rgba(112, 141, 171, 0.18) 0%,
        transparent 50%
      ),
      radial-gradient(
        circle at 85% 100%,
        rgba(201, 169, 110, 0.1) 0%,
        transparent 45%
      );
    pointer-events: none;
  }

  & > * {
    position: relative;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    padding-top: calc(4rem + 50px);
  }
`;

export const Title = styled.h1`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(2.5rem, 5vw, ${({ theme }) => theme.fontSizes["6xl"]});
  font-weight: ${({ theme }) => theme.fontWeights.light};
  color: ${({ theme }) => theme.colors.white};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  letter-spacing: ${({ theme }) => theme.letterSpacings.tight};
`;

export const HeaderSub = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  color: rgba(255, 255, 255, 0.6);
  max-width: 600px;
  margin: 0 auto;
  line-height: ${({ theme }) => theme.lineHeights.relaxed};
`;

/* ── Intro + services ── */
export const IntroGrid = styled.div`
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  gap: ${({ theme }) => theme.spacing["3xl"]};
  align-items: start;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.xl};
  }
`;

export const Lead = styled.div`
  p {
    font-size: ${({ theme }) => theme.fontSizes.lg};
    color: ${({ theme }) => theme.colors.textSecondary};
    line-height: ${({ theme }) => theme.lineHeights.relaxed};
  }

  p + p {
    margin-top: ${({ theme }) => theme.spacing.lg};
  }
`;

export const ServiceList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

export const ServiceItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGray};
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ theme }) => theme.colors.textPrimary};

  &::before {
    content: "";
    flex-shrink: 0;
    width: 8px;
    height: 8px;
    margin-top: 8px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.gold};
  }
`;

export const IntroImage = styled.div`
  margin-top: ${({ theme }) => theme.spacing.xl};
  aspect-ratio: 16 / 11;
  border-radius: ${({ theme }) => theme.borderRadius.lg};

  background-color: ${({ theme }) => theme.colors.navy};
  background-image: ${({ $src }) => ($src ? `url(${$src})` : "none")};
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;

  box-shadow: ${({ theme }) => theme.shadows.md};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    aspect-ratio: 16 / 9;
  }
`;
/* ── Stats ── */
export const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.spacing.xl};
  margin-bottom: ${({ theme }) => theme.spacing["3xl"]};
  padding-bottom: ${({ theme }) => theme.spacing.xl};
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGray};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.lg};
    text-align: center;
  }
`;

export const StatItem = styled.div`
  strong {
    display: block;
    font-family: ${({ theme }) => theme.fonts.display};
    font-size: clamp(2rem, 4vw, ${({ theme }) => theme.fontSizes["4xl"]});
    font-weight: ${({ theme }) => theme.fontWeights.light};
    line-height: 1;
    color: ${({ theme }) => theme.colors.navy};
    margin-bottom: ${({ theme }) => theme.spacing.xs};
  }

  span {
    font-family: ${({ theme }) => theme.fonts.caps};
    font-size: ${({ theme }) => theme.fontSizes.xs};
    font-weight: ${({ theme }) => theme.fontWeights.medium};
    letter-spacing: ${({ theme }) => theme.letterSpacings.wider};
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.gold};
  }
`;

/* ── Process steps ── */
export const StepsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.spacing.xl};

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

export const StepCard = styled.div`
  position: relative;
  overflow: hidden;
  padding: ${({ theme }) => theme.spacing.xl};
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  transition:
    transform ${({ theme }) => theme.transitions.normal},
    box-shadow ${({ theme }) => theme.transitions.normal},
    border-color ${({ theme }) => theme.transitions.normal};

  /* Gold accent bar that sweeps in on hover */
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(
      90deg,
      ${({ theme }) => theme.colors.gold} 0%,
      ${({ theme }) => theme.colors.goldLight} 100%
    );
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.45s cubic-bezier(0.16, 1, 0.3, 1);
  }

  &:hover {
    transform: translateY(-6px);
    border-color: rgba(201, 169, 110, 0.45);
    box-shadow: 0 18px 44px rgba(26, 35, 50, 0.13);
  }
  &:hover::before {
    transform: scaleX(1);
  }
`;

/* Oversized faint numeral behind the card content */
export const StepGhost = styled.span`
  position: absolute;
  top: -0.3em;
  right: 0.08em;
  z-index: 0;
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: 7rem;
  font-weight: ${({ theme }) => theme.fontWeights.light};
  line-height: 1;
  color: ${({ theme }) => theme.colors.navy};
  opacity: 0.05;
  pointer-events: none;
  user-select: none;
`;

export const StepNumber = styled.span`
  position: relative;
  z-index: 1;
  display: inline-block;
  font-family: ${({ theme }) => theme.fonts.caps};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  letter-spacing: ${({ theme }) => theme.letterSpacings.wider};
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.gold};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

export const StepTitle = styled.h3`
  position: relative;
  z-index: 1;
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

export const StepText = styled.p`
  position: relative;
  z-index: 1;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: ${({ theme }) => theme.lineHeights.relaxed};
`;

/* ── Projects gallery ── */
export const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.spacing.xl};

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

export const ProjectCard = styled.article`
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.md};
`;

export const ProjectImage = styled.div`
  aspect-ratio: 4 / 3;

  background-color: ${({ theme }) => theme.colors.navy};
  background-image: ${({ $src }) => ($src ? `url(${$src})` : "none")};
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;

  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.4);
  font-family: ${({ theme }) => theme.fonts.caps};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  text-transform: uppercase;
  letter-spacing: ${({ theme }) => theme.letterSpacings.wider};
`;

export const ProjectBody = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
`;

export const ProjectMeta = styled.p`
  font-family: ${({ theme }) => theme.fonts.caps};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  letter-spacing: ${({ theme }) => theme.letterSpacings.wider};
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.gold};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  min-height: 1em;
`;

export const ProjectTitle = styled.h3`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: ${({ theme }) => theme.fontSizes["2xl"]};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

export const ProjectDesc = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: ${({ theme }) => theme.lineHeights.relaxed};
`;

/* ── CTA ── */
export const CtaSection = styled.section`
  position: relative;
  padding: ${({ theme }) => theme.spacing["4xl"]} 0;
  background: linear-gradient(
      to bottom,
      rgba(16, 22, 34, 0.84) 0%,
      rgba(16, 22, 34, 0.92) 100%
    )
    ${({ $src }) => ($src ? `, url(${$src}) center / cover no-repeat` : "")};
  background-color: ${({ theme }) => theme.colors.navy};
  color: ${({ theme }) => theme.colors.white};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing["3xl"]} 0;
  }
`;

export const Cta = styled.div`
  text-align: center;
`;

export const CtaTitle = styled.h2`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(2rem, 4vw, ${({ theme }) => theme.fontSizes["4xl"]});
  font-weight: ${({ theme }) => theme.fontWeights.light};
  color: ${({ theme }) => theme.colors.white};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

export const CtaText = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  color: rgba(255, 255, 255, 0.6);
  max-width: 520px;
  margin: 0 auto ${({ theme }) => theme.spacing.xl};
  line-height: ${({ theme }) => theme.lineHeights.relaxed};
`;

export const CtaButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.xl};
  background: ${({ theme }) => theme.colors.gold};
  color: ${({ theme }) => theme.colors.navy};
  font-family: ${({ theme }) => theme.fonts.caps};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  letter-spacing: ${({ theme }) => theme.letterSpacings.wide};
  text-transform: uppercase;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  transition: all ${({ theme }) => theme.transitions.normal};

  &:hover {
    background: ${({ theme }) => theme.colors.goldLight};
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(201, 169, 110, 0.35);
  }
`;
